import { Outlet } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { messages } from '../lang/translate';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { useEffect, useState, useContext, useCallback } from 'react';
import { Flags } from './Flags';
import { Displays, DisplaysValues, LangValues } from '../type';
import { Brawler, Map, arrayRandomValue } from '../common.type';
import { LayoutContext } from '../context/LayoutContext';
import { getBrawlers, getMaps } from '../api/api';
import { Footer } from './Footer';
import { DisplayModeContext } from '../context/DisplayModeContext';
import { ensureLang } from '../helpers/ensureLang';
import { Logo } from './Logo';
import { Button } from './Button';
import { Menu } from './Menu';

const bgMove = () => keyframes`
    0% { background-position: 0 0; }
    50% { background-position: 50% -50%; }
    100% {  background-position-y: 0 0; }
`;

const LayoutWrapper = styled.div<{
  $color: string;
  $displayMode: DisplaysValues;
}>`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: auto;
  min-width: 320px;
  background: ${(props) =>
    `url(${process.env.PUBLIC_URL}/images/desktopPatternBgDark.webp) ${props.$color} no-repeat`};
  background-size: cover;
  ${(props) => css`
    animation: ${bgMove}
      ${props.$displayMode === Displays.LANDSCAPE ? `15s` : `40s`} infinite
      linear;
  `}
`;

const LayoutHeader = styled.header`
  position: relative;
  display: flex;
  justify-content: center;
  padding: 2vmin;
`;
const HeaderOptions = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: end;
  gap: 1.5vmin;
  align-items: center;
`;

const Donate = styled.a<{ $displayMode: DisplaysValues }>`
  display: flex;
  color: #fff;
  justify-content: center;
  align-items: center;
  font-size: 5vmin;
  text-decoration: none;
  text-shadow: 0 0.4vmin #000;
  -webkit-text-stroke: 0.03vmin #000;

  &:hover {
    text-decoration: underline;
  }
`;

const ButtonWrapper = styled.div`
  z-index: 2;
`;

const LayoutContent = styled.div`
  display: flex;
  flex: 1;
`;

export const Layout = () => {
  const { displayMode } = useContext(DisplayModeContext);
  const [locale, setLocale] = useState<LangValues>(() => {
    const langStore = localStorage.getItem('language') ?? '';
    if (ensureLang(langStore)) {
      return langStore;
    }

    const lang = navigator.language.split('-')[0] ?? '';
    return ensureLang(lang) ? lang : 'en';
  });
  const [brawlers, setBrawlers] = useState<Brawler[]>([]);
  const [menu, setMenu] = useState<boolean>(false);
  const [maps, setMaps] = useState<Map[]>([]);
  const [palette] = useState<string[]>([
    '#feeaeb',
    '#f8edf3',
    '#daace5',
    '#fcd8c5',
    '#fac491',
    '#f7bb8d',
    '#ff8b5d',
    '#e86363',
    '#ffb600',
    '#01ad32',
    '#00befd',
    '#fd71bc',
    '#842ab2',
    '#334a89',
    '#ded9ed',
  ]);
  const [bgColor] = useState<string>(() => {
    return arrayRandomValue(palette) ?? '#01ad32';
  });

  const onClickLang = useCallback((lang: LangValues) => {
    setLocale(lang);
    localStorage.setItem('language', lang);
  }, []);

  useEffect(() => {
    getBrawlers()
      .then((brawlers) => {
        setBrawlers(brawlers);
      })
      .catch((err: unknown) => {
        console.error(err);
      });
    getMaps()
      .then((maps) => {
        setMaps(maps);
      })
      .catch((err: unknown) => {
        console.error(err);
      });
  }, []);

  return (
    <LayoutContext.Provider value={{ displayMode, brawlers, maps }}>
      <IntlProvider
        messages={messages[locale]}
        locale={locale}
        defaultLocale="en"
      >
        <LayoutWrapper $displayMode={displayMode} $color={bgColor}>
          <LayoutHeader>
            <Logo />
            <HeaderOptions>
              <Donate
                href="https://www.paypal.com/donate/?hosted_button_id=3LW9RNAHGR64G"
                $displayMode={displayMode}
              >
                <FormattedMessage id="donate" />
              </Donate>
              <Flags lang={locale} onClick={onClickLang} />
              <ButtonWrapper>
                <Button
                  color="grey"
                  onClick={() => {
                    setMenu(!menu);
                  }}
                >
                  ☰
                </Button>
              </ButtonWrapper>
            </HeaderOptions>
          </LayoutHeader>
          <LayoutContent>
            <Outlet />
            <Menu
              open={menu}
              onClose={() => {
                setMenu(false);
              }}
            />
          </LayoutContent>
          <Footer displayMode={displayMode} />
        </LayoutWrapper>
      </IntlProvider>
    </LayoutContext.Provider>
  );
};
